import React from 'react';
import { EntityLayout, EntitySwitch } from '@backstage/plugin-catalog';
import { Grid } from '@material-ui/core';
import {
  RELATION_CHILD_OF,
  RELATION_PARENT_OF,
} from '@backstage/catalog-model';
import { Direction, RelationsGraphCard } from '../../cards/RelationsGraphCard';
import { useEntity } from '@backstage/plugin-catalog-react';
import { WarningCard } from '../../cards/WarningCard';
import { GroupProfileCard } from '../../cards/GroupProfileCard';
import { RTLLinksCard } from '../../cards/LinksCard';
import { ContactChannelsCard } from '../../cards/ContactChannelsCard';
import { hasContactChannels, isNotAdGroup } from '../../cards/utils';
import { DepartmentTreeTable } from '../TeamsPage/DepartmentTreeTable';
import {
  CLOUD_TYPES,
  HasFilteredResourcesCard,
  KUBERNETES_TYPES,
  useDepartmentResourceTypes,
} from '../../cards/HasFilteredResourcesCard';
import { EntraGroupsIn } from '../../../hooks';
import { EntraGroupsGrid } from './MemberListCard';

/**
 * A department of the CMDB organisation tree. It owns no products or cloud
 * accounts of its own, everything it has is the sub departments and teams below it.
 *
 * The Kubernetes and the Cloud Accounts tabs therefore show what the teams
 * below it hold, gathered over the whole sub tree. A card with nothing in it
 * leaves itself out, and a tab whose cards would all be empty is not there at
 * all - the `if` of a route only sees the entity, which says nothing about
 * what the teams below hold, so the tabs are gated on what was fetched for the
 * cards instead.
 *
 * While that query is out both tabs are shown. A missing tab is a statement -
 * "this department runs no clusters" - and the page is in no position to make
 * it before the answer arrives. Showing them and dropping the ones that turn
 * out to be empty reads as a page still settling; hiding them and adding them
 * later reads as a finished page that says there is nothing.
 */
export function DepartmentPage() {
  const { entity } = useEntity();
  const { types, loading } = useDepartmentResourceTypes(entity.metadata.name);
  const holdsAnyOf = (candidates: string[]) =>
    loading || candidates.some(type => types.has(type));

  const cmdbGroups = (entity.spec as any)?.cmdbGroups as
    | EntraGroupsIn
    | undefined;

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Overview">
        <Grid container spacing={3} alignItems="stretch">
          <WarningCard />
          <Grid item xs={12} md={6}>
            <GroupProfileCard />
          </Grid>
          <Grid item xs={12} md={6}>
            <RelationsGraphCard
              height={400}
              kinds={['Group']}
              relations={[RELATION_CHILD_OF, RELATION_PARENT_OF]}
              // The org tree around the department, without its Entra groups
              // - see isNotAdGroup.
              entityFilter={isNotAdGroup}
              direction={Direction.TOP_BOTTOM}
            />
          </Grid>
          {/*
           * No contacts cards here: the About grid of the profile card above
           * already lists the technical and the security contacts.
           */}
          <RTLLinksCard />
          {/*
           * No department states a contact channel yet, so this card is not
           * on any page today. It is wired up all the same: the schema has
           * the field, and a department that fills it in should read like a
           * team rather than wait for the page to catch up.
           */}
          <EntitySwitch>
            <EntitySwitch.Case if={hasContactChannels}>
              <Grid item xs={12}>
                <ContactChannelsCard />
              </Grid>
            </EntitySwitch.Case>
          </EntitySwitch>
        </Grid>
      </EntityLayout.Route>
      {/*
        The Entra groups of the department and who is in them, a tab of its own
        for the same reason the team page gives them one: a card per group, each
        of them a member table, is more than the overview can carry.

        Always there, unlike the Kubernetes and Cloud tabs below: every
        department has the technical pair, declared or not, so there is no
        department for which this tab would be empty.
      */}
      <EntityLayout.Route path="/groups" title="Groups">
        <EntraGroupsGrid
          groups={cmdbGroups}
          target={{
            kind: 'department',
            cmdbDepartmentVuid: entity.metadata.name,
          }}
        />
      </EntityLayout.Route>
      <EntityLayout.Route path="/organization" title="Organization">
        <Grid container spacing={3} alignItems="stretch">
          <Grid item xs={12}>
            {/* The tab is the tree and nothing else, so the search box has the
                focus, the way it has on the organization page. */}
            <DepartmentTreeTable
              rootId={entity.metadata.name}
              title="Organization"
              autoFocusSearch
              expandAll
            />
          </Grid>
        </Grid>
      </EntityLayout.Route>
      {holdsAnyOf(KUBERNETES_TYPES) && (
        <EntityLayout.Route path="/kubernetes" title="Kubernetes">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="k8s-cluster"
                title="Kubernetes Clusters"
              />
            </Grid>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="k8s-cluster-slice"
                title="Kubernetes Cluster Slices"
              />
            </Grid>
          </Grid>
        </EntityLayout.Route>
      )}
      {holdsAnyOf(CLOUD_TYPES) && (
        <EntityLayout.Route path="/cloud" title="Cloud Accounts">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="gcp-project"
                title="GCP Projects"
              />
            </Grid>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="aws-account"
                title="AWS Accounts"
              />
            </Grid>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="azure-subscription"
                title="Azure Subscriptions"
              />
            </Grid>
            <Grid item xs={12}>
              <HasFilteredResourcesCard
                type="oci-account"
                title="OCI Accounts"
              />
            </Grid>
          </Grid>
        </EntityLayout.Route>
      )}
    </EntityLayout>
  );
}
